/* ─────────────────────────────────────────────────────────────
   The Instagram feed, fetched and normalised.

   Kept apart from the gallery so the gallery knows nothing about
   Instagram: it renders a list of media, and this is one way of
   producing that list.
   ───────────────────────────────────────────────────────────── */

/**
 * Instagram's shapes, flattened to what the gallery needs.
 *
 * `media_type` alone is not enough to spot a clip: a carousel post whose
 * first child is a video comes back as CAROUSEL_ALBUM. Instagram only
 * sends `thumbnail_url` for media that moves, so its presence is the
 * reliable signal.
 */
function normalise(item) {
    const isVideo = item.media_type === 'VIDEO' || Boolean(item.thumbnail_url);

    return {
        id: item.id || item.media_url,
        type: isVideo ? 'video' : 'image',
        src: item.media_url,
        poster: item.thumbnail_url || item.media_url,
        caption: item.caption || '',
        permalink: item.permalink || '',
        takenAt: item.timestamp ? new Date(item.timestamp) : null
    };
}

/**
 * Newest first.
 *
 * The previous sort was `b.timestamp - a.timestamp`, and Instagram sends
 * `timestamp` as an ISO 8601 string. Subtracting two strings is NaN, a
 * comparator that returns NaN sorts nothing, and the feed came out in
 * whatever order the API happened to return. Comparing parsed dates is
 * what actually puts the newest post first.
 */
function newestFirst(a, b) {
    const at = a.takenAt ? a.takenAt.getTime() : 0;
    const bt = b.takenAt ? b.takenAt.getTime() : 0;

    return bt - at;
}

export async function fetchInstagramMedia(url, { signal } = {}) {
    const response = await fetch(url, { signal });

    if (!response.ok)
        throw new Error(`Instagram feed responded ${response.status}`);

    const payload = await response.json();
    const raw = Array.isArray(payload?.data) ? payload.data : [];

    return raw
        .filter(item => item && item.media_url)
        .map(normalise)
        .sort(newestFirst);
}
